import React, { useState } from 'react';
import {
    Modal,
    Box,
    IconButton,
    Typography,
    Stack,
    Paper,
    Avatar,
    TextField,
    Button,
    LinearProgress,
    Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import LeadService from '../../core/services/leadsService';
import toast from 'react-hot-toast';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
    width: 'auto',
    height: 'auto',
    overflow: 'auto',
};
const AddOrganizationModal = ({ GetAllOrganization }) => {
    const [open, setOpen] = useState(false);
    const [Loading, setLoading] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        formik.resetForm();
        setOpen(false);
    };

    const validationSchema = yup.object({
        organizationName: yup.string().required('Organization name is required'),
        name: yup.string().required('First Name is required'),
        last_name: yup.string().required('Last Name is required'),
        email: yup.string().email('Invalid email format').required('Email is required'),
        phone: yup.string().required('Phone number is required')
            .matches(/^[\d\+\-\(\) ]+$/, 'Phone number can only contain numbers, +, -, (, and )')
            .min(10, 'Phone number must be at least 10 characters')
            .max(10, 'Phone number cannot be more than 10 characters'),
        image: yup.mixed().required('Image is required').test('fileFormat', 'Unsupported file format', value => {
            return value && ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'].includes(value.type);
        }),
    });

    const formik = useFormik({
        initialValues: {
            image: null,
            organizationName: '',
            email: '',
            phone: '',
            name: '',
            last_name: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const res = await LeadService.addOrganization(values);
                if (res?.data?.success === true) {
                    toast.success(res?.data?.message);
                    formik.resetForm();
                    handleClose();
                    GetAllOrganization();
                }
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        },
    });
    return (
        <>
            <Button
                variant="contained"
                onClick={handleOpen}
                className='main-primary-btn'
                startIcon={<AddIcon />}
            >
                Add Organization
            </Button>


            <Modal open={open} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">

                <Box sx={style} className="modal-resp-settings">

                    <IconButton style={{ position: 'absolute', top: '10px', right: '10px' }} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} sx={{ marginTop: '18px' }}>
                        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} className='w-100'>
                            <Box position={'relative'} className='w-100'>
                                <Stack alignItems={'center'}>
                                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                        <div>
                                            <Typography
                                                sx={{ fontSize: '32px', fontWeight: '700', mb: 5 }}
                                            >
                                                Add Organization
                                            </Typography>
                                        </div>


                                    </Box>
                                    <form className='modal-form-settings' onSubmit={formik.handleSubmit}>
                                        <Stack spacing={2}>
                                            <Stack
                                                style={{ display: 'flex', justifyContent: 'center' }}
                                                onClick={() => document.getElementById('image').click()}
                                                sx={{
                                                    '&:hover': {
                                                        cursor: 'pointer',
                                                    },
                                                }}
                                                direction={'row'}
                                                spacing={1}
                                                alignItems={'center'}
                                            >
                                                <Avatar
                                                    sx={{
                                                        border: formik.touched.image && formik.errors.image ? '3px solid #d32f2f' : formik.values.image ? '3px solid green' : '',
                                                        width: '60px',
                                                        height: '60px',
                                                        bgcolor: formik.touched.image && formik.errors.image ? '#d32f2f' : 'rgb(25, 118, 210)',
                                                    }}
                                                    alt="Avatar"
                                                    src={formik.values.image ? URL.createObjectURL(formik.values.image) : ''}
                                                >
                                                    {formik.values.image ? null : <CloudUploadIcon />}
                                                </Avatar>
                                                <Typography color={'gray'}>
                                                    {formik.values.image ? 'Change Image' : 'Upload image'}
                                                </Typography>
                                            </Stack>
                                            {formik.errors.image && <small style={{ color: '#d32f2f' }}>{formik.errors.image}</small>}
                                            <input
                                                style={{ display: 'none' }}
                                                id="image"
                                                name="image"
                                                type="file"
                                                onChange={(event) => {
                                                    const file = event.currentTarget.files[0];
                                                    const allowedFormats = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
                                                    if (file) {
                                                        // Check file type
                                                        if (!allowedFormats.includes(file.type)) {
                                                            formik.setFieldValue('image', null);
                                                            toast.error('Invalid image format. Only jpeg, png, and jpg formats are allowed.');
                                                            return;
                                                        }

                                                        // Check file size
                                                        if (file.size > 250 * 1024) { // 20KB in bytes
                                                            formik.setFieldValue('image', null);
                                                            toast.error('Image size should be less than 250KB.');
                                                            return;
                                                        }

                                                        // If both checks pass, set the field value
                                                        formik.setFieldValue('image', file);
                                                    }
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                id="name"
                                                name="name"
                                                label="First Name"
                                                value={formik.values.name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.name && Boolean(formik.errors.name)}
                                                helperText={formik.touched.name && formik.errors.name}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                id="last_name"
                                                name="last_name"
                                                label="Last Name"
                                                value={formik.values.last_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                                                helperText={formik.touched.last_name && formik.errors.last_name}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            />
                                            <TextField
                                                fullWidth
                                                id="organizationName"
                                                name="organizationName"
                                                label="Organization Name"
                                                value={formik.values.organizationName}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
                                                helperText={formik.touched.organizationName && formik.errors.organizationName}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            />

                                            <TextField
                                                fullWidth
                                                id="email"
                                                name="email"
                                                label="Email"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.email && Boolean(formik.errors.email)}
                                                helperText={formik.touched.email && formik.errors.email}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            />

                                            <TextField
                                                fullWidth
                                                id="phone"
                                                name="phone"
                                                label="Phone"
                                                value={formik.values.phone}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.phone && Boolean(formik.errors.phone)}
                                                helperText={formik.touched.phone && formik.errors.phone}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            />

                                            <Box>
                                                <Button
                                                    variant="contained"
                                                    sx={{
                                                        borderRadius: '12px',
                                                        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                                                        border: '1px solid #587DBF',
                                                        backgroundColor: '#5379BD',
                                                        textTransform: 'capitalize',
                                                        color: 'white',
                                                        fontFamily: 'inherit'
                                                    }}
                                                    fullWidth
                                                    type="submit"
                                                    disabled={Loading} // Disable the button when loading
                                                >
                                                    {Loading ? ' Create...' : ' Create Organization'} {/* Optional: Show loading text */}

                                                </Button>
                                                <LinearProgress sx={{ visibility: Loading ? '' : 'hidden' }} />
                                            </Box>
                                        </Stack>
                                    </form>

                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default AddOrganizationModal;
